import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { Container, CssBaseline, Grid, Paper, Typography } from '@mui/material'
import VideoRecorder from 'components/VideoRecorder'
import React, { useEffect, useState } from 'react'
import * as Device from 'react-device-detect'
import images from 'utils/images'
import { ContributionActions, ContributionStoreStates } from '.'
import { ContributionButton, ContributionImage, ListTitleText } from './styles'

type OwnProps = {}

type ContributionProps = OwnProps & ContributionStoreStates & ContributionActions

const Contribution = (props: ContributionProps) => {
  const { movies, readingWords } = props
  const { loaded, data } = readingWords.contribution
  const { word: uploadsWord, uploading, error } = movies.contribution

  const [recording, setRecording] = useState(false)

  useEffect(() => {
    // 発話ワードの読み込み
    props.loadNextWord()
  }, [])

  if (!loaded) return <></>

  if (!data) return <Typography color="secondary">エラーが発生しました</Typography>

  const { index, word, list } = data

  const setPreviousWord = () => {
    setRecording(false)
    props.loadPreviousWord()
    props.clearContribution()
  }

  const setNextWord = () => {
    setRecording(false)
    props.loadNextWord()
    props.clearContribution()
  }

  const contributeFile = (file: File | Blob | null | undefined) => {
    if (!file || !list || !word) return

    props.contribute({ list, word, file })
  }

  const changeWordButton = (type: 'random' | 'pre' | 'next') => {
    let disabled = uploading
    if (type === 'pre') disabled = disabled || index === 0
    if (type === 'next') disabled = disabled || index === list.numberOfWords - 1

    let icon = <RefreshIcon />
    if (type === 'pre') icon = <ArrowBackIcon />
    if (type === 'next') icon = <ArrowForwardIcon />

    let text = '他の言葉を選ぶ'
    if (type === 'pre') text = '前の言葉'
    if (type === 'next') text = '次の言葉'

    return (
      <ContributionButton
        component="label"
        color="primary"
        disabled={disabled}
        onClick={() => (type === 'pre' ? setPreviousWord() : setNextWord())}>
        {icon}
        {text}
      </ContributionButton>
    )
  }

  const randomButton = changeWordButton('random')
  const previousButton = changeWordButton('pre')
  const nextButton = changeWordButton('next')

  const leftButton = list.isDefault ? <></> : previousButton
  const rightButton = list.isDefault ? randomButton : nextButton

  const isBrowser = Device.isBrowser && !Device.isIOS13 // iPadOS 対策
  const isMobile = Device.isMobile || Device.isIOS13 // iPadOS 対策

  return (
    <Container>
      <CssBaseline />
      <Paper sx={{ p: 1, mb: 2 }}>
        <Grid
          container
          direction="column"
          sx={{
            alignItems: 'center',
          }}>
          <ListTitleText align="left">はじめに</ListTitleText>
          <Grid>
            <Typography variant="body2" align="center">
              認識精度を向上するために発話シーン投稿の協力をお願いしています。
            </Typography>
            <Typography variant="body2" align="center">
              ご協力いただける場合は、以下の言葉を発話したシーンの投稿をお願いします。
            </Typography>
          </Grid>
        </Grid>

        <Grid
          container
          direction="column"
          sx={{
            alignItems: 'center',
          }}>
          <ListTitleText align="left">発話シーン投稿</ListTitleText>
          <Grid>
            {leftButton}
            {rightButton}
          </Grid>
          <Grid
            container
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <Typography variant="h3" align="center" fontWeight="bold">
              {word.text}
            </Typography>
          </Grid>
          <Grid>
            <Typography variant="subtitle1" align="center">
              とカメラに向かって話す動画像をアップロードしてください
            </Typography>
          </Grid>
          {isBrowser && !recording && (
            <Grid>
              <ContributionButton
                variant="outlined"
                component="label"
                color="primary"
                disabled={uploading}
                onClick={() => setRecording(true)}>
                動画像を撮影する
              </ContributionButton>
              <ContributionButton variant="outlined" component="label" color="primary" disabled={uploading}>
                ファイルから選択する
                <input
                  type="file"
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    contributeFile(e.target.files?.item(0))
                  }}
                />
              </ContributionButton>
            </Grid>
          )}
          {isBrowser && recording && (
            <VideoRecorder
              display={recording}
              onPost={(data) => {
                setRecording(false)
                contributeFile(data)
              }}
            />
          )}
          {isMobile && (
            <Grid>
              <ContributionButton variant="outlined" component="label" color="primary" disabled={uploading}>
                投稿する
                <input
                  type="file"
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={(e) => contributeFile(e.target.files?.item(0))}
                />
              </ContributionButton>
            </Grid>
          )}
          {uploading && (
            <Grid>
              <Typography color="primary">アップロード中...</Typography>
            </Grid>
          )}
          {uploadsWord && !uploading && !error && (
            <Grid>
              <Typography color="primary">アップロードが完了しました</Typography>
            </Grid>
          )}
          {error && (
            <Grid>
              <Typography color="secondary">{error}</Typography>
            </Grid>
          )}
          <Grid
            container
            direction="column"
            sx={{
              mt: 2,
              alignItems: 'center',
            }}>
            <Grid>
              <Typography>以下の撮影例を参考にしてください。</Typography>
            </Grid>
            <Grid>
              <ContributionImage src={images.contributionSample} alt="sample" />
            </Grid>
          </Grid>
          <Grid>
            <ul style={{ maxWidth: 600 }}>
              <li>撮影中はカメラを動かさないでください。</li>
              <li>録画開始になってから口を動かし始めてください。</li>
              <li>発話開始の前後は口を閉じてください。</li>
              <li>顔は画面中央に位置するようにして発話してください。</li>
              <li>顔は画面中央を向いて発話してください。</li>
              <li>上の言葉を自然な発話速度・口の動きで発話してください。</li>
              <li>
                早すぎる発話／ゆっくりすぎる発話、口を大きく開けすぎる発話／口の開きが小さい発話は誤認識の原因になります。
              </li>
              <li>
                投稿されたシーンは正しい発話シーンであるかクチヨミチームで確認します。正しい発話シーンの場合、マイページの発話シーン本数が増えます。
              </li>
            </ul>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  )
}

export default Contribution
